import type { CarReport, VehicleDefectProfile } from '../api/types'
import { personalNotes } from '../domain/checklist'
import { Icon } from './Icon'

/** What each filled-in point is called in the card's one-line summary, by point number. */
const TOPICS: Record<number, string> = {
  1: 'merk en model',
  2: 'APK',
  3: 'tellerstand',
  5: 'terugroepacties',
  6: 'import',
  8: 'zwakke plekken van het model',
  10: 'plaats van het chassisnummer',
}

/**
 * The card that leads to the fill-in checklist, below the condition cards.
 *
 * It sits after APK, tellerstand, terugroepacties and objecten because those are the answers the
 * checklist fills in, and a reader who has just worked through them is deciding whether to go and
 * look at the car, which is the moment the checklist is for. The header button is the shortcut for
 * someone who already knows it exists; this card is how everyone else finds it.
 *
 * Kept to two lines and a button: it earns nothing, so it must not push the cards that do further
 * down. The summary names which points are already answered for this car, which is what tells a
 * reader the checklist is about this car rather than a generic list.
 */
export function ChecklistSection({
  report,
  defectProfile,
  onOpen,
}: {
  report: CarReport
  defectProfile?: VehicleDefectProfile
  onOpen: () => void
}) {
  const topics = [...personalNotes(report, defectProfile).keys()]
    .map((number) => TOPICS[number])
    .filter((topic): topic is string => topic !== undefined)

  return (
    <div className="checklist-card">
      <p className="checklist-card__lead">
        Ga je deze auto bekijken? Neem de checklist mee: 25 controles voor thuis, de bezichtiging en de
        proefrit.
        {topics.length > 0 && (
          <>
            {' '}
            <strong>Al ingevuld voor deze auto:</strong> {listInDutch(topics)}.
          </>
        )}
      </p>

      <button className="button" type="button" onClick={onOpen}>
        <Icon name="check" size={16} />
        Open de checklist voor deze auto
      </button>
    </div>
  )
}

function listInDutch(items: string[]): string {
  return items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} en ${items[items.length - 1]}`
}
